/**
 * TabPicker Sentry Configuration
 * 
 * Centralized Sentry error monitoring configuration for the TabPicker application.
 * Initializes Sentry with environment-specific settings, user context integration,
 * and performance monitoring for comprehensive error tracking and debugging.
 * 
 * Features:
 * - Environment-aware initialization (development vs production)
 * - Integration with React Router for navigation tracking
 * - User context integration with authentication system
 * - Performance monitoring and tracing
 * - Custom error filtering and sampling
 * 
 * @module TabPicker.Sentry
 */

import * as Sentry from '@sentry/react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { useEffect } from 'react';

/**
 * Initialize Sentry error monitoring
 * 
 * Configures Sentry with appropriate settings based on environment.
 * Sets up integrations for React Router, performance monitoring,
 * and user session tracking.
 * 
 * @param {string} dsn - Sentry Data Source Name from environment
 * @param {string} environment - Current environment (development/production)
 */
export function initSentry(dsn: string, environment: string) {
    if (!dsn) {
        console.warn('Sentry DSN not provided - error monitoring disabled');
        return;
    }

    // Skip Sentry initialization during tests
    if (typeof window !== 'undefined' && (
        window.location?.href?.includes('vitest') ||
        import.meta.env?.MODE === 'test' ||
        process.env.NODE_ENV === 'test'
    )) {
        console.log('Skipping Sentry initialization in test environment');
        return;
    }

    Sentry.init({
        dsn,
        environment,
        integrations: [
            // React Router integration for navigation tracking
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            // Browser performance monitoring
            Sentry.browserTracingIntegration(),
            // Replay sessions for debugging (production only)
            ...(environment === 'production' ? [Sentry.replayIntegration({
                maskAllText: true,
                blockAllMedia: true,
                maskAllInputs: true,    // Enhanced: Mask all form inputs for privacy
                // Temporarily disable worker security for CSP troubleshooting
                workerUrl: undefined,
            })] : []),
        ],
        
        // Performance monitoring settings - optimized for cost (5% sampling in production)
        tracesSampleRate: environment === 'production' ? 0.05 : 1.0,
        
        // Session replay settings (production only) - cost-optimized for production use
        replaysSessionSampleRate: environment === 'production' ? 0.005 : 0, // 0.5% of sessions
        replaysOnErrorSampleRate: environment === 'production' ? 0.5 : 0,   // 50% of error sessions
        
        // Error filtering
        beforeSend(event, hint) {
            // Filter out development-only errors
            if (environment === 'development') {
                // Skip React HMR errors
                if (hint.originalException?.toString().includes('ChunkLoadError')) {
                    return null;
                }
            }
            
            // Filter out network errors that aren't actionable
            if (event.exception?.values?.[0]?.type === 'NetworkError') {
                return null;
            }
            
            return event;
        },
        
        // Additional configuration
        attachStacktrace: true,
        debug: environment === 'development',
    });
}

/**
 * Set user context for Sentry error tracking
 * 
 * Associates errors with specific users for better debugging
 * and support. Should be called when user authentication state changes.
 * 
 * @param {Object|null} user - User object from authentication context
 * @param {string} user.id - Unique user identifier
 * @param {string} user.email - User email address
 * @param {string} user.name - User display name
 */
export function setSentryUserContext(user: { id: string; email: string; name: string } | null) {
    Sentry.setUser(user ? {
        id: user.id,
        email: user.email,
        username: user.name,
    } : null);
}

/**
 * Capture exception with additional context
 * 
 * Manually report errors to Sentry with custom context and tags.
 * Useful for catching and reporting business logic errors that
 * might not trigger automatic error boundaries.
 * 
 * @param {Error} error - The error object to report
 * @param {Object} context - Additional context for debugging
 * @param {string} context.component - React component where error occurred
 * @param {string} context.action - User action that triggered the error
 * @param {Object} context.metadata - Additional metadata for debugging
 */
export function captureException(
    error: Error, 
    context?: {
        component?: string;
        action?: string;
        metadata?: Record<string, any>;
    }
) {
    Sentry.withScope((scope) => {
        if (context) {
            if (context.component) {
                scope.setTag('component', context.component);
            }
            if (context.action) {
                scope.setTag('action', context.action);
            }
            if (context.metadata) {
                scope.setContext('metadata', context.metadata);
            }
        }
        
        Sentry.captureException(error);
    });
}

/**
 * Add breadcrumb for debugging context
 * 
 * Records user actions and application state changes to provide
 * better context when errors occur. Breadcrumbs help reconstruct
 * the sequence of events leading to an error.
 * 
 * @param {string} message - Description of the action or event
 * @param {string} category - Category for grouping breadcrumbs
 * @param {string} level - Severity level (info, warning, error)
 * @param {Object} data - Additional data about the event
 */
export function addBreadcrumb(
    message: string,
    category: string = 'app',
    level: 'info' | 'warning' | 'error' = 'info',
    data?: Record<string, any>
) {
    Sentry.addBreadcrumb({
        message,
        category,
        level,
        data,
        timestamp: Date.now() / 1000,
    });
}

// Re-export Sentry components for convenience
export const ErrorBoundary = Sentry.ErrorBoundary;
export const withErrorBoundary = Sentry.withErrorBoundary;